import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { Verb } from "@onsip/common/interfaces/verb";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";
import { PageItem } from "../../page-picker.component";
import { TopicFormService } from "../../../../services/topic-form.service";

@Component({
  selector: "onsip-url-picker",
  templateUrl: "./url-picker.component.html",
  styleUrls: ["../../../topic-write-form.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class UrlPickerComponent implements OnInit, OnDestroy {
  @Input() type = "";
  @Input() verbs: Array<Verb> = [];
  @Input() initialValue!: PageItem;
  @Output() urlFormInit: EventEmitter<FormGroup> = new EventEmitter<FormGroup>(true);
  @Output() delete: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild("urlInput", { static: false }) urlInput!: ElementRef<HTMLInputElement>;

  translatedVerbItems: Record<string, string> = {};

  /** Internal form group holding a verb and url value for a page */
  urlForm!: FormGroup;
  /** observable of errors on the form */
  formError: Subject<string> = new Subject<string>();
  /** Object returned from translate service */
  private unsubscriber = new Subscription();

  constructor(
    private translate: TranslateService,
    private topicForm: TopicFormService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.translatedVerbItems[Verb.INCLUDES] = this.translate.instant("ONSIP_I18N.INCLUDES");
    this.translatedVerbItems[Verb.STARTS_WITH] = this.translate.instant("ONSIP_I18N.STARTS_WITH");
    this.translatedVerbItems[Verb.ENDS_WITH] = this.translate.instant("ONSIP_I18N.ENDS_WITH");
    this.translatedVerbItems[Verb.EXACT] = this.translate.instant("ONSIP_I18N.EXACT");
    this.translatedVerbItems[Verb.DOES_NOT_EQUAL] = this.translate.instant("ONSIP_I18N.EXACT");
    this.translatedVerbItems[Verb.DOES_NOT_INCLUDE] = this.translate.instant("ONSIP_I18N.INCLUDES");
    this.urlForm = new FormGroup({
      verb: new FormControl(this.initialValue[0]),
      url: new FormControl(this.initialValue[1], {
        validators: [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(150),
          Validators.pattern("^[^<>]+$")
        ]
      })
    });
    this.urlFormInit.emit(this.urlForm);

    this.unsubscriber.add(
      this.topicForm.getSubmitObs().subscribe(() => {
        this.urlForm.markAllAsTouched();
        this.cdRef.markForCheck();
      })
    );
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  getErrors(): string {
    return this.urlForm.controls.url.hasError("required")
      ? this.translate.instant("ONSIP_I18N.YOU_MUST_ENTER_A_VALUE")
      : this.urlForm.controls.url.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.NO_MORE_THAN_150_CHARACTERS")
      : this.urlForm.controls.url.hasError("pattern")
      ? this.translate.instant("ONSIP_I18N.ANGLE_BRACKETS_NOT_ALLOWED__OR_")
      : this.urlForm.controls.url.hasError("minlength")
      ? this.translate.instant("ONSIP_I18N.AT_LEAST_2_CHARACTERS")
      : this.urlForm.controls.url.hasError("otherError")
      ? "Invalid input"
      : "";
  }

  onDeletePageRule() {
    this.delete.emit();
  }
}
